RivalIQ.apps.Dashboard.module('widgets', function (widgets, App, Backbone, Marionette, $, _) {

    'use strict';

    // This is a base view used by many of our charts. It could probably be
    // simplified and cleaned up significantly, specifically loadData and onAttachLoaded

    // Mixin is like .extends but lets you mix in other modules. They get
    // layered on in order and override each other, but the provided hash always
    // wins.  Overwritten methods are always available from
    // this.mixins.[mixinName]
    //
    // RivalIQ.views.[name] is a collection of loading views that handle
    // calling loadData and its associated lifecycle
    const Chart = widgets.Chart = RivalIQ.views.LayoutView.mixin({
        template: '#widgetMetricChartTemplate',

        mixins: {
            // Requires a dashboardModel (widget or curation) passed to the
            // view on construction. This is done automatically for widget
            // views. Maps the dashboardModel to this.dashboardModel and
            // exposes this.getProps and this.setProps on the view itself
            props: Marionette.Dashboard.mixins.Props,
            companySetChangeReload: RivalIQ.apps.Shell.mixins.CompanySetChangeReload
        },

        dashboardModelEvents: {
            // This calls reload on the view when any props change
            'change': 'reload'
        },

        regions: {
            chart: '.chart-region'
        },

        className: 'chart',

        initialize: function () {

            const me = this;
            _.bindAll(me, 'getChartClassName');
        },

        serializeData: function () {

            const me = this;
            return {
                title: me.getOption('title')
            };
        },

        // This is a very hacky load data method. New charts should probably
        // just write their own simple one. See charts.XY for an example
        loadData: function () {
            const me = this;
            const metricDefinition = me.dashboardModel.metric;

            let dimensions;
            if (me.getProp('dimension')) {
                dimensions = metricDefinition.getDimension(me.getProp('dimension')).getMetrics();
            } else {
                // NOTE we need to apply props changes in cases where the chart
                // widget is a nested definition (such as Twitter Insights
                // Followers)
                const props = me.getProps();
                dimensions = new RivalIQ.collections.MetricDefinitions(
                    me.dashboardModel.metrics.map(function (metric) {
                        // NOTE if there is an `id` property specified in the
                        // attributes we need to make sure that we take it out,
                        // as that will result in metrics getting erroneously
                        // filtered out (keep in mind this should only be
                        // a concern for "legacy" metrics that still use
                        // `concept` for configuration over a Plain Old
                        // Javascript Object (POJO) like Custom Dashboards),
                        // see #22884
                        const attributes = _.omit(metric.attributes, 'id');
                        // NOTE we have to use this static function because
                        // some models do not define their own `build` function
                        return RivalIQ.metric.build(attributes, props);
                    })
                );
            }

            // HACK HACK for loading the latest metric format
            if (dimensions.length && dimensions.first().modern) {
                return dimensions.fetch().done(function (metrics) {
                    me.collection = metrics;
                });
            }

            const attrs = {
                dimension: me.getProp('dimension'),
                companySetDescriptor: me.getProp('companySetDescriptor')
            };

            return RivalIQ.collections.MetricDefinitions.fetch(attrs, [metricDefinition, dimensions])
                .done(function (metrics) {
                    me.model = metrics[0];
                    me.collection = metrics[1] || new Backbone.Collection();

                    const limit = me.getProp('dimensionLimit');

                    if (_.exists(limit)) {
                        me.collection.reset(me.collection.filterValues({
                            limit: limit
                        }));
                    }

                    if (!metricDefinition) {
                        me.model = me.collection.first();
                    }
                });
        },

        onBeforeShowChart: _.noop,

        // This method returns a deferred to deal w/ charts that do not render
        // synchronously. We need this for export so that we don't screenshot
        // until everything is done.
        //
        // This was historically a large problem for pie charts.
        // It may not be technically necessary anymore, though async graph
        // rendering has benefits for user experience.
        showChart: function () {
            const me = this;
            const Type = me.chartType;
            const view = new Type(me.getChartAttributes());

            const deferred = new $.Deferred();
            view.once('render:async', deferred.resolve);

            me.onBeforeShowChart(view);
            me.chart.show(view);

            return deferred;
        },

        getTicks: function () {
            const me = this;

            let model = me.model;
            if (!(model instanceof RivalIQ.models.Metric)) {
                model = me.collection.first();
            }

            return model.getTicks(3);
        },

        getTickValues: function () {

            const me = this;
            return _.invokeMap(me.getTicks(), 'format', 'MM/DD/YY');
        },

        getChartAttributes: function () {
            const me = this;

            const data = me.collection.invoke('getChartData');
            // TODO BUG BUG FILTER OTHER

            let concept = me.model;
            if (!(concept instanceof RivalIQ.models.Metric)) {
                concept = me.collection.first();
            }


            let ticks = me.getProp('ticks');
            if (!ticks && concept.get('useIntervalDomainFormatting')) {
                // These charts look best with 9 or fewer ticks; ideally the chart itself would know how to figure that out, but our current generation of charts doesn't.
                const timePeriod = me.getProp('timePeriod');
                const numDataPoints = RivalIQ.utils.timePeriod.getPeriodCount(moment.utc(timePeriod.mainPeriodStart), moment.utc(timePeriod.mainPeriodEnd), RivalIQ.enums.timePeriod.interval[me.getProp('interval')]);

                // This is pretty hacky; our tick positioning code makes bad choices when numTicks is 7 and the domain range is 8 or 9.
                ticks = (numDataPoints < 10) ? numDataPoints : 7;
            }

            return {
                concept: concept,
                metrics: me.collection,
                classNames: me.getChartClassName,
                data: data,
                annotations: me.getAnnotations(),
                tickValues: me.getTickValues(),
                ticks: ticks,
                scale: me.getProp('scale'),
                labels: me.dashboardModel.labels,
                tooltip: (me.getTooltip ? me.getTooltip() : null),
                height: me.getProp('height')
            };
        },

        getAnnotations: function () {
            return [];
        },

        getMetricClassNames: function () {

            const me = this;
            return me.collection.invoke('getClassName');
        },

        getChartClassName: function () {
            const me = this;
            return me.getMetricClassNames();
        },

        // This method is called after this view has been shown to the user.
        // Note that this is not the same thing as after the view has been
        // rendered (use onRenderLoaded for that).
        // We use onAttachLoaded here b/c many of our d3 views need to know the
        // width of the container they are rendering into, and that only works
        // if the view is in the dom and not hidden. Definite room for
        // improvement here
        onAttachLoaded: function () {
            const me = this;
            const view = me.chart.currentView;

            if (!view || !me.chartType.reloadable) {
                return me.showChart();
            }

            _.extend(view.options, me.getChartAttributes());
            // NOTE this is a big hammer, but it's the best that we've got to
            // clear popovers that might get left around after a render outside
            // of the Marionette lifecycle (see #23360)
            $('.popover').popover('destroy');
            view.refresh();
        },

        refresh: _.noop
    });

    /**
     * Gets bar chart class name(s).
     * @param {object} chart - The chart instance.
     * @param {number} i - The metric class name index.
     * @param {number} j - The data ordinal (typically corresponding to a day).
     * @returns {string} Class name(s).
     */

    Chart.TrailingChart = {
        getChartClassName: function (chart, i, j) {
            let concept = chart.model;
            if (!(concept instanceof RivalIQ.models.Metric)) {
                concept = chart.collection.first();
            }
            return (concept.isTrailing(j) ? 'deemphasized' : null);
        }
    };

    // NOTE this is a stepping stone transformer for old style metrics that do
    // not use protoype inheritance to keep metric attributes up to date.
    //
    // For those metrics we must copy props like timePeriod onto the metric
    // instances whenever those properties change
    Chart.buildMetrics = function (widget, props) {
        // TODO this should be handled via prototype inheritance
        const additionalMetricProps = _.pick(props, 'timePeriod', 'companySetDescriptor', 'focusCompanyId', 'activeSources', 'interval');

        const overrideFocusCompany = !widget.getCuration().isCompanySetFilteringDisabled();
        if (widget.metric) {
            if (!widget.metric.build) {
                _.extend(widget.metric.attributes, additionalMetricProps);
            } else if (widget.metric.get('companyId')) {
                if (overrideFocusCompany) {
                    additionalMetricProps.companyId = additionalMetricProps.focusCompanyId;
                }
                widget.metric = new RivalIQ.models.MetricDefinition(_.extend({}, widget.metric.attributes, additionalMetricProps));
            }
        } else if (props.metric && (!widget.metric || !widget.metric.build)) {
            widget.metric = new RivalIQ.models.MetricDefinition(_.extend({}, props.metric, additionalMetricProps));
        }

        if (widget.metrics) {
            widget.metrics.each(function (metric) {
                if (!metric.build) {
                    _.extend(metric.attributes, additionalMetricProps);
                }

                // The `overrideFocusCompany` check here is because we don't want to override the companyId with the
                // companyId implied by the company set in view state if this curation doesn't care about company sets.
                if (!widget.isExporting() && metric.get('companyId') && overrideFocusCompany) {
                    metric.attributes.companyId = RivalIQ.state.utils.getFocusedCompanyId();
                }
            });
        } else if (props.metrics) {
            const metricsProps = _.map(props.metrics, function (metricProps) {
                return _.extend({}, metricProps, additionalMetricProps);
            });

            widget.metrics = new RivalIQ.collections.MetricDefinitions(metricsProps);
        }

        // respect export configuration, else update to current focused company id
        if (!widget.isExporting() && overrideFocusCompany) {
            props.focusCompanyId = RivalIQ.state.utils.getFocusedCompanyId();
        }

        return props;
    };

});
